import type { QueryClient } from "@tanstack/react-query";
import type {
  EmailAccount,
  EmailAccountListItem,
  EmailAccountStatusCounts,
  ListEmailAccountsParams,
  PaginatedEmailAccounts,
  StatusBucket,
  Tag,
} from "@/lib/email-accounts";
import type { EmailAccountStatusValue } from "./search-schema";
import { accountStatusBucket } from "./account-status";
import { WITHOUT_TAGS_FILTER_VALUE } from "./tags/tag-helpers";

/*
 * Patches a SINGLE updated row into the cached list instead of refetching the
 * whole table, deriving `counts`/`total` from the bucket move. Only `status`
 * and `tags` are re-checked client-side — the rest live in the server's
 * `applyExtendedFilters` SQL, so a row that leaves one of those stays visible
 * until the next natural refetch (stale membership, never stale contents).
 * Row-count-changing ops (delete, connect) still need a full invalidate.
 */

/** `setup` is a real bucket with no count key, so counts writes must skip it
 * rather than write an `undefined`/`NaN` into the envelope. */
type CountedBucket = Exclude<StatusBucket, "setup">;

function isCountedBucket(bucket: StatusBucket): bucket is CountedBucket {
  return bucket !== "setup";
}

/** OR-match over tag names, case-insensitive, mirroring the backend's filter.
 * The `WITHOUT_TAGS_FILTER_VALUE` sentinel matches an untagged row, so sentinel
 * + a real name is a union, not a contradiction. */
export function rowMatchesTagFilter(
  rowTags: Tag[],
  filterTags: string[] | undefined,
): boolean {
  if (!filterTags || filterTags.length === 0) return true;
  const wanted = new Set(filterTags.map((t) => t.toLowerCase()));
  if (wanted.has(WITHOUT_TAGS_FILTER_VALUE) && rowTags.length === 0) return true;
  return rowTags.some((t) => wanted.has(t.name.toLowerCase()));
}

/** An absent or empty `status` filter means "no filter", never "matches
 * nothing". */
export function rowMatchesStatusFilter(
  bucket: StatusBucket,
  filterStatus: EmailAccountStatusValue[] | undefined,
): boolean {
  if (!filterStatus || filterStatus.length === 0) return true;
  return (filterStatus as readonly string[]).includes(bucket);
}

/**
 * Move one row between count pills. A row that left the filtered set entirely
 * (`stillMatchesNonStatus === false`) also leaves `all`; a plain bucket change
 * is a pure transfer. Clamped at 0 — a negative pill is a visible bug where a
 * one-off undercount is not.
 */
export function applyCountsDelta(
  counts: EmailAccountStatusCounts | undefined,
  prevBucket: StatusBucket,
  nextBucket: StatusBucket,
  stillMatchesNonStatus: boolean,
): EmailAccountStatusCounts | undefined {
  if (!counts) return counts;
  if (stillMatchesNonStatus && prevBucket === nextBucket) return counts;

  const out = { ...counts };
  if (isCountedBucket(prevBucket)) {
    out[prevBucket] = Math.max(0, out[prevBucket] - 1);
  }
  if (!stillMatchesNonStatus) {
    out.all = Math.max(0, out.all - 1);
    return out;
  }
  if (isCountedBucket(nextBucket)) {
    out[nextBucket] = out[nextBucket] + 1;
  }
  return out;
}

/**
 * Splice one server-confirmed account into every cached list page holding it.
 * An entry that does NOT hold the row is left alone — it is either a different
 * page or a filter the row never matched, and we cannot tell which.
 * `listsKey` is a param, not an import, to avoid a cycle with `queries.ts`.
 */
export function patchAccountInLists(
  qc: QueryClient,
  listsKey: readonly unknown[],
  account: EmailAccount,
): void {
  const nextBucket = accountStatusBucket(account);

  for (const [key] of qc.getQueriesData<PaginatedEmailAccounts>({
    queryKey: listsKey,
  })) {
    // `emailAccountKeys.list(params)` is ['email-accounts', 'list', params].
    const params = (key[2] ?? {}) as ListEmailAccountsParams;

    qc.setQueryData<PaginatedEmailAccounts>(key, (prev) => {
      if (!prev) return prev;
      const index = prev.items.findIndex((row) => row.id === account.id);
      if (index === -1) return prev;

      const prevRow = prev.items[index];
      const prevBucket = accountStatusBucket(prevRow);
      // Merge rather than replace: `EmailAccount` is a superset of the slim
      // `EmailAccountListItem`, but spreading it wholesale would clobber a
      // list-only field with `undefined` the day the two shapes drift.
      const nextRow: EmailAccountListItem = { ...prevRow, ...account };

      const matchesNonStatus = rowMatchesTagFilter(nextRow.tags, params.tags);
      const matchesStatus = rowMatchesStatusFilter(nextBucket, params.status);
      const visible = matchesNonStatus && matchesStatus;

      return {
        ...prev,
        items: visible
          ? prev.items.map((row, i) => (i === index ? nextRow : row))
          : prev.items.filter((_, i) => i !== index),
        total: visible ? prev.total : Math.max(0, prev.total - 1),
        counts: applyCountsDelta(
          prev.counts,
          prevBucket,
          nextBucket,
          matchesNonStatus,
        ) as EmailAccountStatusCounts,
      };
    });
  }
}

/**
 * Carry an already-fetched result set across a page-size change, since `limit`
 * is in the query key and 25 → 10 would otherwise always refetch. Only seeds
 * when the cached page provably holds everything: `items.length === total` and
 * `total <= nextLimit`. `dataUpdatedAt` is carried over rather than stamped to
 * now — faking freshness would let page-size toggling hold stale rows forever.
 */
export function seedResizedPageFromCache(
  qc: QueryClient,
  sourceKey: readonly unknown[],
  destKey: readonly unknown[],
  nextLimit: number,
): boolean {
  // Already cached (the user toggled back to a size they used before) — leave
  // the existing entry and its own freshness alone.
  if (qc.getQueryData(destKey) !== undefined) return false;

  const state = qc.getQueryState<PaginatedEmailAccounts>(sourceKey);
  const data = state?.data;
  if (!data) return false;
  if (data.items.length !== data.total) return false;
  if (data.total > nextLimit) return false;

  qc.setQueryData<PaginatedEmailAccounts>(
    destKey,
    { ...data, page: 1, limit: nextLimit },
    { updatedAt: state?.dataUpdatedAt },
  );
  return true;
}
